import { Col, Form, Row } from 'react-bootstrap';
import { useFormik } from 'formik';
import { Button, DateInput, Input, SelectGeneric } from 'ypf-pas-general-ui-components';
import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';

import { Line } from '../../../utils/globalPropTypes';

import {
  SupportingDocumentSchema,
  SupportingDocumentMonthlySchema
} from './SupportingDocumentSchema';

import './SupportingDocument.scss';

const SupportingDocumentForm = ({
  data,
  loading,
  locationOptions,
  selectedLocation,
  selectedDate,
  minDate,
  maxDate,
  monthly,
  isMultiple,
  onDelete,
  validateData,
  onValidate,
  onCancel,
  onSave
}) => {
  const calculateDates = () => {
    if (isMultiple) {
      return {
        dateFrom: new Date(minDate).toISOString().split('T')[0],
        dateTo: new Date(maxDate).toISOString().split('T')[0]
      };
    }

    return {
      dateFrom: new Date(selectedDate || data?.fecha).toISOString().split('T')[0],
      dateTo: new Date(selectedDate || data?.fecha).toISOString().split('T')[0],
      selectedDate: new Date(selectedDate || data?.fecha).toISOString().split('T')[0]
    };
  };

  const initalValuesFormik = {
    identifier: data?.documentoRespaldo || '',
    locations: [],
    ...calculateDates()
  };

  const readOnly = data?.documentoRespaldo && !data?.editaDocumentoRespaldo;

  const { values, setFieldValue, errors, setFieldTouched, touched, isValid } = useFormik({
    initialValues: initalValuesFormik,
    validationSchema: monthly ? SupportingDocumentMonthlySchema : SupportingDocumentSchema
  });

  const handleDelete = () => {
    onDelete?.(values);
  };

  const handleSave = () => {
    onSave?.(values);
  };

  const deleteDisabled =
    loading ||
    errors?.dateFrom ||
    errors?.dateTo ||
    !validateData?.existeDocumentoRespaldo ||
    !validateData?.tienePartesNotificados;
  const saveDisabled =
    loading ||
    !isValid ||
    !!Object.keys(errors).length ||
    !Object.keys(touched).length ||
    !validateData?.tienePartesNotificados;

  const renderLocationSelected = () => {
    if (!selectedLocation) {
      return <></>;
    }

    return (
      <div>
        <p>Ubicación</p>
        <p className="daily_modal_text fw-bold">
          <span>{selectedLocation?.nombre}</span>
        </p>
      </div>
    );
  };

  const renderTitle = () => (
    <div>
      {monthly && renderLocationSelected()}
      <div>
        <p className="daily_modal_text">
          {readOnly
            ? 'Documento de respaldo para la Posición'
            : 'Registrar documento de respaldo para la Posición '}
          <span className="fw-bold"> {data?.positionName}</span> y Linea
          <span className="fw-bold"> {data?.lineName}</span>
        </p>
      </div>
    </div>
  );

  const renderTitleMultipleModal = () => (
    <div>
      <div>
        <p className="daily_modal_text">
          Registrar documento de respaldo para todas las posiciones y lineas de:
        </p>
      </div>
      {!monthly && renderLocationSelected()}
    </div>
  );

  useEffect(() => {
    if (!readOnly && values.dateFrom && values.dateTo) {
      onValidate?.({
        dateFrom: values.dateFrom,
        dateTo: values.dateTo,
        locations: values?.locations || []
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.dateFrom, values.dateTo, values?.locations, readOnly]);

  const renderUbicaciones = useMemo(() => {
    if (!monthly || !isMultiple) {
      return <></>;
    }

    return (
      <Row>
        <Col sm={12}>
          <SelectGeneric
            checkListOptions
            clearable
            defaultValueOneOption={false}
            disabled={readOnly}
            errors={errors?.locations}
            isDisabled={!values?.locations}
            isInvalid={!!(errors?.locations && touched?.locations)}
            isMulti
            label="Ubicaciones"
            name="locations"
            onChange={(value) => {
              setFieldValue('locations', value);
              setFieldTouched('locations');
            }}
            optionMapper={{
              value: 'id',
              label: 'nombre'
            }}
            options={locationOptions}
            placeholder="Seleccionar ubicación"
            value={values?.locations}
            withSelect="100%"
          />
        </Col>
      </Row>
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors?.locations, locationOptions, monthly, touched?.locations, values?.locations]);

  const renderDates = useMemo(() => {
    if (readOnly) {
      return (
        <DateInput
          disabled
          errors={errors?.selectedDate}
          isInvalid={!!errors?.selectedDate && touched?.selectedDate}
          label="Fecha"
          min={minDate}
          name="selectedDate"
          value={values.selectedDate}
          widthDateInput="50%"
        />
      );
    }

    return (
      <>
        <p className="daily_modal_text">Seleccione rango de fecha a aplicar</p>
        <Row className="mt-0 mb-4">
          <Col sm={6}>
            <DateInput
              disabled={readOnly}
              errors={errors?.dateFrom}
              isInvalid={!!errors?.dateFrom && touched?.dateFrom}
              label="Desde"
              max={values.dateTo}
              min={minDate}
              name="dateFrom"
              onChange={(e) => {
                setFieldTouched('dateFrom');
                setFieldValue('dateFrom', e.target.value);
              }}
              placeholder="Selecciona una fecha"
              value={values.dateFrom}
              widthDateInput="100%"
            />
          </Col>
          <Col sm={6}>
            <DateInput
              disabled={readOnly}
              errors={errors?.dateTo}
              isInvalid={!!errors?.dateTo && touched?.dateFrom}
              label="Hasta"
              max={maxDate}
              min={values.dateFrom}
              minDate={values.dateFrom}
              name="dateTo"
              onChange={async (e) => {
                setFieldTouched('dateTo');
                setFieldValue('dateTo', e.target.value);
              }}
              placeholder="Selecciona una fecha"
              value={values.dateTo}
              widthDateInput="100%"
            />
          </Col>
        </Row>
      </>
    );
  }, [
    errors?.dateFrom,
    errors?.dateTo,
    errors?.selectedDate,
    maxDate,
    minDate,
    readOnly,
    setFieldTouched,
    setFieldValue,
    touched?.dateFrom,
    touched?.selectedDate,
    values.dateFrom,
    values.dateTo,
    values.selectedDate
  ]);

  return (
    <Form>
      {isMultiple ? renderTitleMultipleModal() : renderTitle()}
      <Input
        className="w-50 "
        disabled={readOnly}
        errors={errors.identifier}
        isInvalid={!!errors.identifier && touched.identifier}
        label="Documento de Respaldo"
        maxLength={30}
        name="identifier"
        onChange={(e) => {
          setFieldTouched('dateFrom');
          setFieldValue('identifier', e.target.value);
        }}
        placeholder="Ingrese el documento de respaldo"
        type="input"
        value={values.identifier}
      />
      {!readOnly && <p className="mt-1">Hasta 30 caracteres</p>}
      {renderUbicaciones}
      {renderDates}
      {validateData?.mensaje && <p className="document-info">{validateData?.mensaje}</p>}
      {readOnly ? (
        <div className="d-flex justify-content-center mt-5">
          <Button
            className="save-button"
            label="Salir"
            onClick={onCancel}
            type="button"
            variant="primary"
          />
        </div>
      ) : (
        <Row className="mt-5">
          <Col className="d-flex justify-content-start">
            <Button
              className={`${deleteDisabled && 'disabled-button'}`}
              disabled={deleteDisabled}
              label="Quitar existente"
              onClick={handleDelete}
              type="button"
              variant="primary"
            />
          </Col>
          <Col className="d-flex justify-content-end">
            <Button
              className="cancel-button"
              label="Cancelar"
              onClick={onCancel}
              type="button"
              variant="light"
            />
            <Button
              className={`save-button ${saveDisabled && 'disabled-button'}`}
              disabled={saveDisabled}
              label={isMultiple ? 'Aplicar carga masiva' : 'Guardar'}
              onClick={handleSave}
              type="button"
              variant="primary"
            />
          </Col>
        </Row>
      )}
    </Form>
  );
};

export default SupportingDocumentForm;

SupportingDocumentForm.propTypes = {
  data: Line,
  onCancel: PropTypes.func,
  onSave: PropTypes.func,
  onDelete: PropTypes.func,
  onValidate: PropTypes.func,
  locationOptions: PropTypes.arrayOf(PropTypes.shape({})),
  selectedLocation: PropTypes.shape({
    nombre: PropTypes?.string
  }),
  monthly: PropTypes.bool,
  loading: PropTypes.bool,
  isMultiple: PropTypes.bool,
  validateData: PropTypes.shape({
    tienePartesNotificados: PropTypes.bool,
    existeDocumentoRespaldo: PropTypes.bool,
    mensaje: PropTypes.string
  }),
  selectedDate: PropTypes.string,
  maxDate: PropTypes.string,
  minDate: PropTypes.string
};

SupportingDocumentForm.defaultProps = {
  data: {
    positionName: '',
    lineName: '',
    idParteDiario: 0,
    estadoEnum: 0,
    tienePuntosPrograma: false
  },
  validateData: {
    tienePartesNotificados: false,
    existeDocumentoRespaldo: false,
    mensaje: null
  },
  monthly: false,
  loading: false,
  isMultiple: false,
  locationOptions: [],
  selectedLocation: null,
  onCancel: () => {},
  onSave: () => {},
  onDelete: () => {},
  onValidate: () => {},
  selectedDate: null,
  maxDate: null,
  minDate: null
};
